
/**
 * @file dcp-client/worker/supervisor2/diagnostics.js
 *
 * A diagnostic support file for Supervisor.
 *
 * @author Paul, paul@distributive.network
 * @date June-July 2024
 * @module diagnostics
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 */
// @ts-check
'use strict';

const { assert } = require('dcp/common/dcp-assert');
const { DCPError } = require('dcp/common/dcp-error');
const utils = require('dcp/utils');

/**
 * HACK: Could not make this type a private static member of Diagnostics,
 * because JSDoc is buggy junk.
 */
class DoNotEmitError extends Error
{
  constructor (error)
  {
    super();
    this.name = 'DoNotEmitError';
    Diagnostics.constructDoNotEmitError(this, error);
  }
}

//
// Rules:
// (1) Never modify an instance of Error.
// (2) Never modify a property of any error-like object.
//
  
class Diagnostics
{
  /**
   * HACK and SAVE: This is how I wanted to define an internal Error type Diagnostics.#DoNotEmitError,
   * but jsdoc is too buggy to understand the syntax.
   */
  // static #DoNotEmitError = class extends Error
  // {
  //   constructor (error)
  //   {
  //     super();
  //     this.name = 'DoNotEmitError';
  //     Diagnostics.#copyAllProperties(this, error);
  //   }
  // }

  /**
   * Use this error to propagate control flow on an error path, but not emit Supervisor.error messages.
   * Should only be used when errors have already been emitted.
   * @param {Error|{ message: string }} error - an error like object use for debugging
   * @returns {Error}
   */
  static doNotEmitError (error)
  {
    return new DoNotEmitError(error);
  }

  /**
   * Detect DoNotEmitError.
   * @param {*} error
   * @returns {boolean}
   */
  static isDoNotEmitError (error)
  {
    return (error instanceof DoNotEmitError);
  }

  /**
   * Decide whether to show error to client.
   * Designed for when this decision is ambiguous.
   * @param {*} error
   */
  static isSyntaxReferenceOrTypeError (error)
  {
    if (typeof error === 'string')
      return Boolean(error.match(/(SyntaxError|ReferenceError|TypeError)/g));
    else if (error instanceof SyntaxError || error instanceof ReferenceError || error instanceof TypeError)
      return true;
    else if (error.name && (error.name === 'SyntaxError' || error.name === 'ReferenceError' || error.name === 'TypeError'))
      return true;
    return false;
  }

  /**
   * Shallow copy of all properties from source --> target.
   * Should usually make sure source is not shared or at least never modify any properties.
   * @param {*} target
   * @param {*} source
   */
  static #copyAllProperties (target, source)
  {
    const pds = Object.getOwnPropertyDescriptors(source);
    for (let propName in pds)
      pds[propName].enumerable = true
    Object.defineProperties(target, pds);
  }

  /**
   * HACK: Expose private #copyAllProperties only for type DoNotEmitError.
   * Finish construction of DoNotEmitError.
   * @param {DoNotEmitError} doNotEmitError
   * @param {Error}          error
   */
  static constructDoNotEmitError (doNotEmitError, error)
  {
    if (Diagnostics.isDoNotEmitError(doNotEmitError))
      return Diagnostics.#copyAllProperties(doNotEmitError, error);
    throw new Error('doNotEmitError must be an instance of DoNotEmitError');
  }

  /**
   * ErrorPayload has these additional properties and other error shaped objects may also.
   * @param {{ message: string }} targetError
   * @param {{ message: string }} sourceError
   */
  static #transferSelectErrorProperties (targetError, sourceError)
  {
    /** @todo Should we modify DCPError to handle these properties? */
    const props = [ 'operation', 'type', 'origin', 'errorCode' ];
    for (const propName of props)
      if (sourceError[propName])
        targetError[propName] = sourceError[propName];
  }

  /**
   * Transform Error shaped object errorShape into DCPError.
   * When createClone is true, do a structuredClone to handle the case that errorShape is shared
   * and returned dcpError may have properties modified.
   * @param {{ message: string }} errorShape
   * @returns {Error}
   */
  static #handleErrorShapes (errorShape, createClone = false)
  {
    assert(!(errorShape instanceof Error))

    const dcpError = new DCPError(errorShape?.message ? errorShape.message : 'Unknown error');
    if (createClone)
    {
      // There can be uncommon situations where we don't want to modify errorShape because it could be shared.
      // Cannot yet use structuredClone on AggregateError or WebAssembly errors like:
      //   WebAssembly.CompileError, WebAssembly.LinkError, and WebAssembly.RuntimeError
      //   https://github.com/whatwg/html/pull/5749
      errorShape = structuredClone(errorShape);
    }
    Diagnostics.#copyAllProperties(dcpError, errorShape);
    return dcpError;
  }

  /**
   * Error feedback to user.
   * Forms:
   *     error(message, coreError[, true]) -- where coreError is Error-like
   *     error(message, additionalInfo) -- where additionalInfo is convertiable to a string
   * Note:
   *     dcpError=this.error(msg, error, false)
   * has dcpError.stack === `DCPError: ${msg}\nRethrow ${error.stack}`
   * @param {string}  message
   * @param {*}       [coreError=null] - could be Array<Error>|Error|string|ErrorPayload
   * @param {boolean} [displayTopOfStack=alse]
   */
  static error (message, coreError = null, displayTopOfStack = false)
  {
    if (Diagnostics.isDoNotEmitError(coreError))
      return;
    if (coreError instanceof AggregateError)
      return coreError.errors.flat().map((err) => this.error(message, err, displayTopOfStack));

    //console.debug('Diagnostics.error:', message, 'coreError', coreError, 'dts', displayTopOfStack); // SAVE
    if (coreError?.['message'])
    {
      if (!(coreError instanceof Error))
        coreError = Diagnostics.#handleErrorShapes(coreError);
      const dcpError = new DCPError(message, coreError, displayTopOfStack);
      Diagnostics.#transferSelectErrorProperties(dcpError, coreError);
      return dcpError;
    }
    else
    {
      let additionalInfo = coreError;
      if (!additionalInfo)
        additionalInfo = null;
      else if (typeof additionalInfo === 'object')
        additionalInfo = utils.stringify(additionalInfo, -1, 0); // utils.stringify allows object graphs with cycles.
      else if (typeof additionalInfo !== 'string')
        additionalInfo = String(additionalInfo);
      return new DCPError(message, '', additionalInfo);
    }
  }

  /**
   * Experimental: eventually integrate into all worker error handling.
   * Emit error as directly as possible to the worker error event.
   *
   * When (error instanceof AggregateError), recursively process each element of error.errors.
   * else when error is Error-like, transform into a DCPError before emitting the worker 'error' event.
   * else when (error instanceof Error), pass directly to the worker 'error' event emitted.
   * @param {{ message: string }|string} error - error object that is the main content.
   * @param {string}             [info] - addition information
   * @returns {*}
   */
  static directError (error, info)
  {
    if (typeof error === 'string')
      error = { message: error };
    if (typeof error === 'object')
    {
      if (Diagnostics.isDoNotEmitError(error))
        return;
      if (error instanceof AggregateError)
        return error.errors.flat().map((err) => this.directError(err, info));
      if (info)
        error['info'] = info;
      if (!(error instanceof Error))
        return Diagnostics.#handleErrorShapes(error);
    }
    return error;
  }
}
exports.Diagnostics = Diagnostics;
